import { useMemo, useRef, useState, type ChangeEvent, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  CalendarDays,
  Check,
  FileUp,
  Flag,
  Layers,
  Loader2,
  MessageSquareText,
  Paperclip,
  Tag,
  UserPlus,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  DOC_TYPE_LABEL,
  PRODUCT_DEV_PRIORITIES,
  PRODUCT_DEV_STATUSES,
  type ProductDevPriority,
} from '@/features/product-dev/utils/status-style';
import { NON_DOCUMENT_DOC_TYPES } from '@/features/product-dev/utils/doc-types';
import {
  MAX_PRODUCT_DEV_UPLOAD_BYTES,
  formatProductDevUploadBytes,
  prepareProductDevUpload,
  productDevFileToBase64,
} from '@/features/product-dev/utils/file-upload';

const UNCHANGED = '__unchanged__';
const CLEAR = '__clear__';
const SET = '__set__';

export type ProductDevBatchUpdates = {
  priority?: ProductDevPriority | null;
  status?: string;
  expected_completion_date?: string | null;
  assigned_to?: string | null;
  brand?: string | null;
  capsule?: string | null;
  archive?: true;
};

export type ProductDevBatchApply = {
  updates: ProductDevBatchUpdates;
  note: string | null;
  mentions: string[];
  attachment: {
    docType: string;
    fileName: string;
    contentType: string | null;
    body: string;
  } | null;
};

type Member = { id: string; label: string };
type MentionablePerson = Member & { organizationName?: string | null };

const DOCUMENT_TYPES = Object.keys(DOC_TYPE_LABEL).filter(
  // sample_photo belongs to the sample/hero workflow and is intentionally
  // hidden from the Files card; offering it here would make a successful
  // batch upload appear to vanish.
  (docType) => docType !== 'sample_photo' && !NON_DOCUMENT_DOC_TYPES.includes(docType),
);

export function BatchUpdateSheet({
  open,
  onOpenChange,
  selectedProducts,
  members,
  mentionablePeople,
  mentionablePeopleLoading,
  pending,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedProducts: Array<{ id: string; styleName: string }>;
  members: Member[];
  mentionablePeople: MentionablePerson[];
  mentionablePeopleLoading: boolean;
  pending: boolean;
  onApply: (payload: ProductDevBatchApply) => Promise<void>;
}) {
  const { t } = useTranslation('productDev');
  const selectedCount = selectedProducts.length;
  const [note, setNote] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [status, setStatus] = useState(UNCHANGED);
  const [priority, setPriority] = useState(UNCHANGED);
  const [assignee, setAssignee] = useState(UNCHANGED);
  const [dateMode, setDateMode] = useState(UNCHANGED);
  const [expectedDate, setExpectedDate] = useState('');
  const [brandMode, setBrandMode] = useState(UNCHANGED);
  const [brand, setBrand] = useState('');
  const [capsuleMode, setCapsuleMode] = useState(UNCHANGED);
  const [capsule, setCapsule] = useState('');
  const [docType, setDocType] = useState('supplier_correspondence');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [preparingFile, setPreparingFile] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Inline @-mention picker — same interaction contract as the single-record
  // comment composer (CommentTimeline), so tagging works identically whether
  // one product or fifty is selected. Selections land in the same `mentions`
  // state the "Notify people" popover manages.
  const noteRef = useRef<HTMLTextAreaElement | null>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);

  const mentionMatches = useMemo(() => {
    const term = mentionFilter.trim().toLowerCase();
    // No result cap (matches CommentTimeline): slicing to 5 hid everyone past
    // the first five alphabetical names — the dropdown scrolls instead.
    // Matching also covers organizationName so typing the factory name
    // surfaces its people.
    if (!term) return mentionablePeople;
    return mentionablePeople.filter(
      (person) =>
        person.label.toLowerCase().includes(term) ||
        (person.organizationName ?? '').toLowerCase().includes(term),
    );
  }, [mentionablePeople, mentionFilter]);

  const handleNoteChange = (value: string) => {
    setNote(value);
    // Track the typed prefix after the last `@` before the caret — keeps the
    // picker open while the user types and closes it on whitespace.
    const caret = noteRef.current?.selectionStart ?? value.length;
    const upToCaret = value.slice(0, caret);
    const at = upToCaret.lastIndexOf('@');
    if (at < 0) {
      setMentionOpen(false);
      return;
    }
    const prefix = upToCaret.slice(at + 1);
    if (/[\s]/.test(prefix) || prefix.length > 30) {
      setMentionOpen(false);
      return;
    }
    setMentionFilter(prefix);
    setMentionIndex(0); // re-anchor on the most relevant match as the filter changes
    setMentionOpen(true);
  };

  const insertMention = (person: MentionablePerson) => {
    const caret = noteRef.current?.selectionStart ?? note.length;
    const before = note.slice(0, caret);
    const after = note.slice(caret);
    const at = before.lastIndexOf('@');
    if (at < 0) return;
    setNote(`${before.slice(0, at)}@${person.label} ${after}`);
    setMentions((current) => (current.includes(person.id) ? current : [...current, person.id]));
    setMentionOpen(false);
    setMentionFilter('');
    // Restore caret position just after the inserted handle.
    requestAnimationFrame(() => {
      const t = noteRef.current;
      if (!t) return;
      const pos = at + 1 + person.label.length + 1;
      t.focus();
      t.setSelectionRange(pos, pos);
    });
  };

  const reset = () => {
    setNote('');
    setMentions([]);
    setMentionOpen(false);
    setMentionFilter('');
    setMentionIndex(0);
    setStatus(UNCHANGED);
    setPriority(UNCHANGED);
    setAssignee(UNCHANGED);
    setDateMode(UNCHANGED);
    setExpectedDate('');
    setBrandMode(UNCHANGED);
    setBrand('');
    setCapsuleMode(UNCHANGED);
    setCapsule('');
    setDocType('supplier_correspondence');
    setAttachmentFile(null);
    setPreparingFile(false);
    setFileInputKey((current) => current + 1);
  };

  const handleOpenChange = (next: boolean) => {
    if (pending) return;
    onOpenChange(next);
    if (!next) reset();
  };

  const updates = useMemo<ProductDevBatchUpdates>(() => {
    const next: ProductDevBatchUpdates = {};
    if (status !== UNCHANGED) next.status = status;
    if (priority === CLEAR) next.priority = null;
    else if (priority !== UNCHANGED) next.priority = priority as ProductDevPriority;
    if (assignee === CLEAR) next.assigned_to = null;
    else if (assignee !== UNCHANGED) next.assigned_to = assignee;
    if (dateMode === CLEAR) next.expected_completion_date = null;
    else if (dateMode === SET && expectedDate) next.expected_completion_date = expectedDate;
    if (brandMode === CLEAR) next.brand = null;
    else if (brandMode === SET && brand.trim()) next.brand = brand.trim();
    if (capsuleMode === CLEAR) next.capsule = null;
    else if (capsuleMode === SET && capsule.trim()) next.capsule = capsule.trim();
    return next;
  }, [assignee, brand, brandMode, capsule, capsuleMode, dateMode, expectedDate, priority, status]);

  const noteValue = note.trim();
  const hasUpdates = Object.keys(updates).length > 0;
  const invalidSetValue =
    (dateMode === SET && !expectedDate) ||
    (brandMode === SET && !brand.trim()) ||
    (capsuleMode === SET && !capsule.trim());
  const canApply = selectedCount > 0
    && !invalidSetValue
    && !preparingFile
    && (hasUpdates || noteValue.length > 0 || attachmentFile !== null);

  const summary = [
    noteValue ? `Add one note to ${selectedCount} product timelines` : null,
    attachmentFile
      ? t('list.batch.sheet.attachmentSummary', {
          name: attachmentFile.name,
          count: selectedCount,
        })
      : null,
    status !== UNCHANGED ? 'Change status' : null,
    priority !== UNCHANGED ? 'Change priority' : null,
    assignee !== UNCHANGED ? 'Change assignee' : null,
    dateMode !== UNCHANGED ? 'Change expected date' : null,
    brandMode !== UNCHANGED ? 'Change brand' : null,
    capsuleMode !== UNCHANGED ? 'Change capsule' : null,
  ].filter((item): item is string => Boolean(item));

  const toggleMention = (id: string) =>
    setMentions((current) =>
      current.includes(id) ? current.filter((memberId) => memberId !== id) : [...current, id],
    );

  const selectAttachment = async (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.files?.[0];
    if (!raw) return;
    setPreparingFile(true);
    try {
      const { file, conversionFailed } = await prepareProductDevUpload(raw);
      if (conversionFailed) {
        toast({
          title: t('detail.toast.heicFailed.title'),
          description: t('detail.toast.heicFailed.desc'),
        });
      }
      if (file.size > MAX_PRODUCT_DEV_UPLOAD_BYTES) {
        toast({
          title: t('detail.toast.fileTooLarge.title', {
            size: formatProductDevUploadBytes(file.size),
          }),
          description: t('detail.toast.fileTooLarge.desc', {
            max: formatProductDevUploadBytes(MAX_PRODUCT_DEV_UPLOAD_BYTES),
          }),
          variant: 'destructive',
        });
        setAttachmentFile(null);
        setFileInputKey((current) => current + 1);
        return;
      }
      setAttachmentFile(file);
    } finally {
      setPreparingFile(false);
    }
  };

  const handleApply = async () => {
    let attachment: ProductDevBatchApply['attachment'] = null;
    if (attachmentFile) {
      try {
        const { body, type } = await productDevFileToBase64(attachmentFile);
        attachment = {
          docType,
          fileName: attachmentFile.name,
          contentType: type,
          body,
        };
      } catch (error) {
        toast({
          title: t('list.batch.sheet.attachmentReadFailed'),
          description: error instanceof Error ? error.message : t('list.toast.tryAgain'),
          variant: 'destructive',
        });
        return;
      }
    }
    await onApply({
      updates,
      note: noteValue || null,
      mentions: noteValue ? mentions : [],
      attachment,
    });
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-6 py-5 pr-12">
          <SheetTitle>Update selected products</SheetTitle>
          <SheetDescription>
            Review one coordinated update for exactly {selectedCount} selected product{selectedCount === 1 ? '' : 's'}.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-6 px-6 py-5">
            <section className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 rounded-md bg-threadheads-purple/10 p-2 text-threadheads-purple">
                  <MessageSquareText className="h-4 w-4" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="text-sm font-medium">Add a shared note</h3>
                  <p className="text-xs text-muted-foreground">
                    A separate comment is added to each product so its timeline stays complete.
                  </p>
                </div>
              </div>
              <div className="relative">
                <Textarea
                  ref={noteRef}
                  value={note}
                  onChange={(event) => handleNoteChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (mentionOpen && event.key === 'Escape') {
                      event.preventDefault();
                      setMentionOpen(false);
                      return;
                    }
                    // While the picker is open it owns Enter/Tab/arrows; the
                    // highlighted match is inserted without reaching for the mouse.
                    if (mentionOpen && mentionMatches.length > 0) {
                      if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        setMentionIndex((i) => (i + 1) % mentionMatches.length);
                        return;
                      }
                      if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
                        return;
                      }
                      if ((event.key === 'Enter' && !event.metaKey && !event.ctrlKey) || event.key === 'Tab') {
                        event.preventDefault();
                        const pick = mentionMatches[mentionIndex] ?? mentionMatches[0];
                        if (pick) insertMention(pick);
                      }
                    }
                  }}
                  placeholder="e.g. Please use the updated cuff measurement across this capsule. Type @ to mention."
                  maxLength={8000}
                  rows={5}
                  className="resize-none"
                />
                {mentionOpen && mentionMatches.length > 0 && (
                  <div className="absolute top-full left-0 z-20 mt-1 max-h-64 w-72 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
                    {mentionMatches.map((person, idx) => (
                      <button
                        type="button"
                        key={person.id}
                        onClick={() => insertMention(person)}
                        onMouseEnter={() => setMentionIndex(idx)}
                        // Keep the keyboard-highlighted row visible now that
                        // the uncapped list scrolls. Optional call: jsdom
                        // (unit tests) has no scrollIntoView.
                        ref={(el) => {
                          if (idx === mentionIndex) el?.scrollIntoView?.({ block: 'nearest' });
                        }}
                        aria-selected={idx === mentionIndex}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted',
                          idx === mentionIndex && 'bg-muted',
                        )}
                      >
                        <span className="min-w-0 flex-1 truncate">{person.label}</span>
                        {/* External-org marker — whose Daniel this is must be
                            visible at the moment of choosing. ring-1 keeps the
                            chip's silhouette on the highlighted row, whose
                            background is also bg-muted. */}
                        {person.organizationName && (
                          <span className="max-w-[96px] shrink-0 truncate rounded-sm bg-muted px-1.5 py-px text-[10px] text-muted-foreground ring-1 ring-border">
                            {person.organizationName}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5 text-xs"
                      disabled={!noteValue || mentionablePeopleLoading || mentionablePeople.length === 0}
                    >
                      {mentionablePeopleLoading
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Bell className="h-3.5 w-3.5" />}
                      {mentions.length > 0
                        ? t('list.batch.sheet.notifyCount', { count: mentions.length })
                        : t('list.batch.sheet.notifyPeople')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-72 p-2">
                    <p className="px-2 pb-2 text-xs text-muted-foreground">
                      {t('list.batch.sheet.notifyHelp')}
                    </p>
                    <div className="max-h-64 space-y-0.5 overflow-y-auto">
                      {mentionablePeople.map((member) => (
                        <label
                          key={member.id}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted"
                        >
                          <Checkbox
                            checked={mentions.includes(member.id)}
                            onCheckedChange={() => toggleMention(member.id)}
                          />
                          <span className="min-w-0 flex-1 truncate">{member.label}</span>
                          {member.organizationName && (
                            <span className="max-w-28 truncate rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                              {member.organizationName}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {note.length.toLocaleString()} / 8,000
                </span>
              </div>
            </section>

            <section className="space-y-3 border-t pt-5">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 rounded-md bg-threadheads-purple/10 p-2 text-threadheads-purple">
                  <Paperclip className="h-4 w-4" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="text-sm font-medium">{t('list.batch.sheet.attachmentTitle')}</h3>
                  <p className="text-xs text-muted-foreground">
                    {t('list.batch.sheet.attachmentHelp', { count: selectedCount })}
                  </p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="h-9" aria-label={t('list.batch.sheet.documentType')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`docType.${value}`, { defaultValue: DOC_TYPE_LABEL[value] })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div>
                  <input
                    key={fileInputKey}
                    ref={fileInputRef}
                    id="product-dev-batch-file"
                    type="file"
                    className="sr-only"
                    onChange={(event) => void selectAttachment(event)}
                    disabled={pending || preparingFile}
                  />
                  {attachmentFile ? (
                    <div className="flex h-9 items-center gap-2 rounded-md border bg-muted/20 px-3 text-sm">
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate" title={attachmentFile.name}>
                        {attachmentFile.name}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {formatProductDevUploadBytes(attachmentFile.size)}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        aria-label={t('list.batch.sheet.removeAttachment')}
                        onClick={() => {
                          setAttachmentFile(null);
                          setFileInputKey((current) => current + 1);
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-9 w-full justify-start gap-2 font-normal"
                      disabled={preparingFile}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {preparingFile
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <FileUp className="h-3.5 w-3.5" />}
                      {preparingFile
                        ? t('list.batch.sheet.preparingAttachment')
                        : t('list.batch.sheet.chooseAttachment')}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('list.batch.sheet.attachmentLimit', {
                  max: formatProductDevUploadBytes(MAX_PRODUCT_DEV_UPLOAD_BYTES),
                })}
                {' '}{t('list.batch.sheet.attachmentVisibility')}
              </p>
            </section>

            <section className="space-y-3 border-t pt-5">
              <div>
                <h3 className="text-sm font-medium">Change fields at the same time</h3>
                <p className="text-xs text-muted-foreground">
                  Leave a field on “No change” to preserve each product’s current value.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <FieldSelect
                  icon={Check}
                  label="Status"
                  value={status}
                  onValueChange={setStatus}
                  options={PRODUCT_DEV_STATUSES.map((value) => ({ value, label: titleCase(value) }))}
                />
                <FieldSelect
                  icon={Flag}
                  label="Priority"
                  value={priority}
                  onValueChange={setPriority}
                  options={[
                    ...PRODUCT_DEV_PRIORITIES.map((value) => ({ value, label: titleCase(value) })),
                    { value: CLEAR, label: 'Clear priority' },
                  ]}
                />
                <FieldSelect
                  icon={UserPlus}
                  label="Assignee"
                  value={assignee}
                  onValueChange={setAssignee}
                  options={[
                    ...members.map((member) => ({ value: member.id, label: member.label })),
                    { value: CLEAR, label: 'Unassign' },
                  ]}
                />
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CalendarDays className="h-3.5 w-3.5" /> Expected date
                  </Label>
                  <Select value={dateMode} onValueChange={setDateMode}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNCHANGED}>No change</SelectItem>
                      <SelectItem value={SET}>Set a date</SelectItem>
                      <SelectItem value={CLEAR}>Clear date</SelectItem>
                    </SelectContent>
                  </Select>
                  {dateMode === SET && (
                    <Input
                      type="date"
                      value={expectedDate}
                      onChange={(event) => setExpectedDate(event.target.value)}
                      className="h-9"
                      aria-label="Expected completion date"
                    />
                  )}
                </div>
                <TextFieldChange
                  icon={Tag}
                  label="Brand"
                  mode={brandMode}
                  onModeChange={setBrandMode}
                  value={brand}
                  onValueChange={setBrand}
                  placeholder="Brand name"
                />
                <TextFieldChange
                  icon={Layers}
                  label="Capsule"
                  mode={capsuleMode}
                  onModeChange={setCapsuleMode}
                  value={capsule}
                  onValueChange={setCapsule}
                  placeholder="Capsule name"
                />
              </div>
            </section>

            <section className="rounded-lg border bg-muted/30 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Review
              </h3>
              {summary.length > 0 ? (
                <ul className="mt-3 space-y-2 text-sm">
                  {summary.map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">Choose a note or field change to continue.</p>
              )}
              <div className="mt-4 border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground">Selected products</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {selectedProducts.slice(0, 8).map((product) => (
                    <span
                      key={product.id}
                      title={product.styleName}
                      className="max-w-[24ch] truncate rounded-full border bg-background px-2.5 py-1 text-xs"
                    >
                      {product.styleName}
                    </span>
                  ))}
                  {selectedProducts.length > 8 && (
                    <span className="rounded-full border border-dashed px-2.5 py-1 text-xs text-muted-foreground">
                      +{selectedProducts.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            </section>
          </div>
        </ScrollArea>

        <SheetFooter className="border-t bg-background px-6 py-4 sm:items-center sm:justify-between sm:space-x-3">
          <p className="text-xs text-muted-foreground">
            Selected products are the complete scope of this update.
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={pending}>
              Cancel
            </Button>
            <Button
              type="button"
              className="min-w-36 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              disabled={!canApply || pending}
              onClick={() => void handleApply()}
            >
              {pending ? 'Applying…' : `Update ${selectedCount} product${selectedCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function FieldSelect({
  icon: Icon,
  label,
  value,
  onValueChange,
  options,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={UNCHANGED}>No change</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function TextFieldChange({
  icon: Icon,
  label,
  mode,
  onModeChange,
  value,
  onValueChange,
  placeholder,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  mode: string;
  onModeChange: (value: string) => void;
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </Label>
      <Select value={mode} onValueChange={onModeChange}>
        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={UNCHANGED}>No change</SelectItem>
          <SelectItem value={SET}>Set {label.toLowerCase()}</SelectItem>
          <SelectItem value={CLEAR}>Clear {label.toLowerCase()}</SelectItem>
        </SelectContent>
      </Select>
      {mode === SET && (
        <Input
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          placeholder={placeholder}
          maxLength={120}
          className="h-9"
          aria-label={label}
        />
      )}
    </div>
  );
}

const titleCase = (value: string) =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
